import { motion, useAnimation, cubicBezier } from 'framer-motion';
import { useState, useRef, useEffect, MutableRefObject } from 'react';

import { Button, Box, useColorModeValue } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import { SparkleIconMotion } from './SparkleIconMotion';

const MotionBox = motion(Box);

const START_ANIMATION_POSITION = 'translate(-150%, -50%)';
const MIDDLE_ANIMATION_POSITION = 'translate(-50%, -50%)';
const END_ANIMATION_POSITION = 'translate(30%, -50%)';

export const AiActionBarButton = ({
  onClick,
  label = translate('Vimeo AI'),
  isDisabled = false,
  buttonRef,
  testId,
  variant = 'minimal',
}: {
  onClick?: () => void;
  label?: string;
  isDisabled?: boolean;
  buttonRef?: MutableRefObject<HTMLButtonElement | null>;
  testId?: string;
  variant?: 'minimal' | 'secondary';
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isMountWidthAnimationDone, setIsMountWidthAnimationDone] = useState(false);

  const controlWrapper = useAnimation();
  const controlGradientWrapper = useAnimation();

  const prevHoveringState = useRef<boolean>(false);

  const hoverOpacityByTheme = useColorModeValue(0.7, 1);

  useEffect(() => {
    //Mount animation
    (async () => {
      await Promise.all([
        controlWrapper.start(
          {
            width: '100%',
          },
          {
            duration: 0.8,
          },
        ),
        controlGradientWrapper.start(
          {
            transform: MIDDLE_ANIMATION_POSITION,
            opacity: hoverOpacityByTheme,
          },
          {
            duration: 0.8,
          },
        ),
      ]);

      setIsMountWidthAnimationDone(true);

      await controlGradientWrapper.start(
        {
          opacity: 0,
        },
        {
          duration: 0.8,
        },
      );

      await controlGradientWrapper.start(
        {
          transform: START_ANIMATION_POSITION,
        },
        {
          duration: 0.8,
        },
      );
    })();
  }, [controlGradientWrapper, controlWrapper, hoverOpacityByTheme]);

  useEffect(() => {
    //On hover animation
    (async () => {
      if (isHovering) {
        controlGradientWrapper.start(
          {
            transform: MIDDLE_ANIMATION_POSITION,
            opacity: hoverOpacityByTheme,
          },
          {
            duration: 0.4,
            ease: cubicBezier(0.17, 0.0, 0.2, 1),
          },
        );
      } else if (!isHovering && prevHoveringState.current) {
        await controlGradientWrapper.start(
          {
            transform: END_ANIMATION_POSITION,
            opacity: 0,
          },
          {
            duration: 0.4,
            ease: 'easeIn',
          },
        );

        await controlGradientWrapper.start(
          {
            transform: START_ANIMATION_POSITION,
          },
          {
            duration: 0,
          },
        );
      }

      prevHoveringState.current = isHovering;
    })();
  }, [controlGradientWrapper, hoverOpacityByTheme, isHovering]);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <MotionBox
      tabIndex={0}
      position="relative"
      zIndex={0}
      backgroundColor="transparent"
      _hover={{
        backgroundColor: 'transparent !important',
      }}
      overflow="hidden"
      borderRadius="6px"
      width={0}
      outlineColor={'blue.500'}
      outlineOffset={2}
      animate={controlWrapper}
      onTouchEnd={handleMouseLeave}
      onPointerLeave={handleMouseLeave}
      onPointerEnter={handleMouseEnter}
    >
      <MotionBox
        height={'300%'}
        width={'300%'}
        position="absolute"
        filter={'blur(12px)'}
        opacity={0}
        zIndex={1}
        initial={{
          transform: START_ANIMATION_POSITION,
        }}
        borderRadius="50%"
        animate={controlGradientWrapper}
      >
        <Box
          height={'100%'}
          width={'100%'}
          backgroundSize={'100% 100%'}
          backgroundImage={
            'radial-gradient(#82FFEB 0%, #17D5FF 30%, #0BA1FF 45%, #9E00FF 75%, #9E00FF 0%);'
          }
        />
      </MotionBox>
      <Button
        data-testid={testId}
        ref={buttonRef}
        onClick={onClick}
        zIndex={2}
        variant={variant}
        tabIndex={-1}
        _hover={{
          backgroundColor: 'transparent !important',
        }}
        _active={{
          color: 'text-primary',
        }}
        size="sm"
        leftIcon={
          <SparkleIconMotion
            isMountWidthAnimationDone={isMountWidthAnimationDone}
            isHovered={isHovering}
          />
        }
        isDisabled={isDisabled}
      >
        {label}
      </Button>
    </MotionBox>
  );
};
